/**
 * Minimal Tailwind-styled button: primary (filled), secondary (outline),
 * ghost (text-only), danger (filled, destructive). The full design system
 * arrives later.
 */

import type { ButtonHTMLAttributes } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
}

// Ring on `focus-visible` (keyboard) only — `focus:` fired the ring on
// mouse-click too, which read as a jarring detached black border (the
// accent is near-black in the Default theme + the 2px offset) especially
// on a button sitting at a section edge.
const baseClasses =
  'inline-flex cursor-pointer items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-60'

const variantClasses: Record<Variant, string> = {
  primary: 'bg-accent text-accent-fg hover:opacity-90 active:opacity-80',
  // Destructive actions. A real variant rather than `className="bg-red-600"`
  // on a primary button: that only overrode the BACKGROUND and left
  // `text-accent-fg`, which is near-black on both dark themes — 3.88:1 on
  // Obsidian and 3.17:1 on Midnight, under the 4.5:1 AA floor. It also
  // relied on stylesheet order to beat `bg-accent`, since there is no
  // tailwind-merge here.
  danger: 'bg-danger text-danger-fg hover:opacity-90 active:opacity-80',
  secondary:
    'border border-border-strong bg-bg-elevated text-fg hover:bg-bg-muted active:bg-bg-muted',
  ghost: 'bg-transparent text-fg hover:bg-bg-muted active:bg-bg-muted',
}

export function Button({
  variant = 'primary',
  className = '',
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = `${baseClasses} ${variantClasses[variant]} ${className}`.trim()
  return <button type={type} className={classes} {...rest} />
}
